import { ng } from '@uirouter/angularjs/lib/angular';
import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'underscore';
import { HttpService } from '../../../../app.common/modules/api/http.service';
import { BlobService } from '../../../../app.common/modules/common/services/blob.service';
import { EnumService } from '../../../../app.common/modules/common/services/enum.service';
import { UrlService } from '../../../../app.common/modules/common/services/url.service';
import { CommonCampaignsService } from '../../../../app.retailer.common/modules/services/commonCampaigns.service';
import { AccountService } from '../../account/account.service';
import { RetailerService } from '../../api/retailer.service';
import {
    AudienceOffer,
    AudienceOfferCategoryListItem,
    AudienceOfferPlaceListItem,
    AudienceResourceOfferModel,
    Campaign,
    CampaignKeyword,
    CampaignListItem,
    CampaignProductLink,
    CampaignsPagingResult,
    EnumListItem,
    StoreCampaigns,
    UploadedCampaignImage,
    VideoMetadata
} from '../../core';
import { ShopService } from '../../places/shop.service';
import {
    IAudienceOffer,
    ICampaign,
    ICampaignImage,
    ICampaignListItem,
    ICampaignProductLink,
    ICampaignSchedule,
    ICampaignVideo,
    ICategoryAudience,
    IGroupAudience,
    IGroupsRule,
    IKeyword,
    IOfferAudience,
    IPlaceAudience,
    IPlacesGroupAudience,
    IRule,
    ISimpleAudience
} from '../models';

export class CampaignsService {
    public static $inject = ['$timeout', 'http', '$q', '$uibModal', 'retailerService', 'shopService',
        'commonCampaignsService', 'enumService', 'blobService', 'urlService', 'accountService'];

    private initialCampaignImageData: { id: number; base64: string } = null;
    private editableCampaign: ICampaign = null;
    private campaignModel: ICampaign = {
        id: null,
        name: null,
        additionalInfo: null,
        nfcStickers: [],
        entityStatus: Campaign.EntityStatusEnum.NUMBER_1,
        campaignContentType: 1,
        simpleAudienceMode: true,
        jispExclusive: false,
        geoTargeting: false,
        pushNotification: null,
        type: this.enumService.CampaignType.Offer,
        image: null,
        video: null,
        videoStatus: null,
        audience: {
            mode: '',
            groups: null as IGroupsRule,
            rules: null as IRule[]
        },
        schedule: this.getDefaultCampaignSchedule(),
        statusModel: this.getDefaultStatusModel(),
        createDate: null,
        isExclusiveOffer: false,
        keywords: [],
        campaignProductLinks: [],
        currencySign: '',
        offerAudience: this.getClientAudience(null),
        campaignInformationCardLinks: []
    };

    constructor(
        private $timeout: ng.ITimeoutService,
        private http: HttpService,
        private $q: ng.IQService,
        private $uibModal: ng.ui.bootstrap.IModalService,
        private retailerService: RetailerService,
        private shopService: ShopService,
        private commonCampaignsService: CommonCampaignsService,
        private enumService: EnumService,
        private blobService: BlobService,
        private urlService: UrlService,
        private accountService: AccountService
    ) {
        $(window).resize(this.resizeFont);
    }

    public getAuthorizationBearer() {
        return { Authorization: 'Bearer ' + this.accountService.getRetailerToken() };
    }

    public getRetailerCampaigns = (offset, limit, sorting) => {
        return this.http.get<CampaignsPagingResult>(this.retailerService.retailerCampaigns(offset, limit, sorting));
    }

    public getStoresCampaigns = () => {
        return this.http.get<StoreCampaigns[]>(this.retailerService.storesCampaigns());
    }

    public getRetailCampaignsPreview = async () => {
        return this.getCampaignsPreview(this.retailerService.retailerCampaigns(0, 0, { ID: 'ascending' }));
    }

    public getRetailCampaign = async (id: number) => {
        const campaign = await this.http.get<CampaignListItem>(this.retailerService.retailerCampaign(id));
        this.commonCampaignsService.adaptCampaignData(campaign);
        return campaign;
    }

    public getCampaign = async (id: number) => {
        const campaign = await this.http.get<Campaign>(this.retailerService.campaign(id));
        this.initialCampaignImageData = null;
        return this.toClientCampaign(id, campaign);
    }

    public createCampaign = async (campaign: ICampaign, image: ICampaignImage, video: ICampaignVideo) => {

        if (this.isNeedUploadImage(campaign.campaignContentType, image)) {
            const uploadedImage = await this.uploadImage(image);
            campaign.image = {
                imageID: uploadedImage.ID,
                imageUrl: this.retailerService.getCampaignImageUrl(uploadedImage.ID),
                croppedImage: null,
                fileName: null,
                isGif: uploadedImage.Type === UploadedCampaignImage.TypeEnum.NUMBER_4
            };
            image.imageID = campaign.image.imageID;
            image.imageUrl = campaign.image.imageUrl;
            campaign.campaignContentType = uploadedImage.Type;
        }

        if (campaign.campaignContentType === this.enumService.CampaignContentType.Video && this.isSetCampaingVideo(video)) {
            campaign.video = {
                ID: video.ID,
                VideoUrl: video.VideoUrl,
                TeaserUrl: null,
                ThumbnailUrl: video.ThumbnailUrl,
                Width: video.Width,
                Height: video.Height,
                videoID: video.videoID,
                campaignStatus: video.campaignStatus
            };
        }

        return await this.http.post<number>(this.retailerService.campaign(), this.toServerCampaign(campaign));
    }

    public toServerCampaign = (model: ICampaign): Campaign => {
        return {
            Name: model.name,
            NfcStickers: model.nfcStickers,
            PushNotification: model.pushNotification,
            StartDate: model.schedule.hasStartDate ? this.asUtc(this.commonCampaignsService.getDateTime(model.schedule.startDate.toString(), '00:00')) : null,
            EndDate: model.schedule.hasEndDate ? this.asUtc(this.commonCampaignsService.getDateTime(model.schedule.endDate.toString(), '00:00')) : null,
            AlertDate: model.schedule.hasAlertDate ? this.asUtc(this.commonCampaignsService.getDateTime(model.schedule.alertDate.toString(), '00:00')) : null,
            Description: this.replaceTags(model.additionalInfo),
            EntityStatus: model.entityStatus as Campaign.EntityStatusEnum,
            ImageID: model.campaignContentType === this.enumService.CampaignContentType.Video ? null : model.image ? model.image.imageID : null,
            VideoID: model.campaignContentType !== this.enumService.CampaignContentType.Video ? null : model.video ? model.video.videoID : null,
            DailyStartTime: model.schedule.allDayStart && model.schedule.allDayEnd ? null : this.commonCampaignsService.getTimeString(model.schedule.allDayStart, new Date(model.schedule.startTime.toString())),
            DailyEndTime: model.schedule.allDayEnd && model.schedule.allDayStart ? null : this.commonCampaignsService.getTimeString(model.schedule.allDayEnd, new Date(model.schedule.endTime.toString())),
            DaysOfWeek: model.schedule.hasDaily ? null : model.schedule.daysOfWeekInt as Campaign.DaysOfWeekEnum,
            Type: model.type as Campaign.TypeEnum,
            JispExclusive: model.jispExclusive,
            GeoTargeting: model.geoTargeting,
            SimpleAudienceMode: model.simpleAudienceMode,
            IsExclusiveOffer: model.isExclusiveOffer,
            Keywords: _.map(model.keywords, (item): CampaignKeyword => {
                return {
                    Keyword: item.keyword,
                    ID: item.id
                };
            }),
            CampaignProductLinks: _.map(model.campaignProductLinks, (link): CampaignProductLink => {
                return {
                    ID: link.id,
                    ProductID: link.productId
                };
            }),
            CampaignInformationCardLinks: model.campaignInformationCardLinks,
            ContentType: model.campaignContentType as Campaign.ContentTypeEnum,
            Audience: {
                Ages: model.offerAudience.ages,
                Genders: model.offerAudience.genders,
                Interests: model.offerAudience.interests,
                PlaceIDs: model.offerAudience.placeIDs,
                CategoryIDs: model.offerAudience.categoryIDs
            }
        };
    }

    public toClientCampaign = async (id: number, campaign: Campaign) => {
        const campaignInformationCardLinks = angular.copy(campaign.CampaignInformationCardLinks);
        const model: ICampaign = await this.getDefaultCampaignModel(campaign.Type);
        model.id = id;
        model.additionalInfo = campaign.Description;
        model.name = campaign.Name;
        model.nfcStickers = campaign.NfcStickers;
        model.geoTargeting = campaign.GeoTargeting;
        model.jispExclusive = campaign.JispExclusive;
        model.pushNotification = campaign.PushNotification;
        model.statusModel.set(campaign.Status, campaign.StatusMessage);
        model.entityStatus = campaign.EntityStatus;
        model.isExclusiveOffer = campaign.IsExclusiveOffer;
        model.campaignProductLinks = _.map(campaign.CampaignProductLinks, (link): ICampaignProductLink => {
            return {
                id: link.ID,
                productId: link.ProductID,
                title: link.Title,
                imageId: link.ImageID,
                minPrice: link.MinPrice,
                maxPrice: link.MaxPrice,
                imageUrl: this.retailerService.getProductImageUrl(link.ImageID, 60, 60),
                isDraft: link.IsDraft,
                currencySign: link.CurrencySign
            };
        });
        model.campaignInformationCardLinks = campaignInformationCardLinks;
        model.keywords = _.map(campaign.Keywords, (item): IKeyword => {
            return {
                id: item.ID,
                keyword: item.Keyword,
                error: null
            };
        });
        if (campaign.ImageID) {
            model.image = {
                imageID: campaign.ImageID,
                imageUrl: this.retailerService.getCampaignImageUrl(campaign.ImageID),
                croppedImage: null,
                fileName: null,
                isGif: campaign.ContentType === UploadedCampaignImage.TypeEnum.NUMBER_4
            };
        }

        if (campaign.VideoID && campaign.Video) {
            model.video = {
                ID: campaign.VideoID.toString(),
                VideoUrl: campaign.Video.VideoUrl || this.retailerService.getCampaignVideoContentUrl(campaign.VideoID),
                TeaserUrl: null,
                ThumbnailUrl: this.retailerService.getCampaignThumbnailImageUrl(campaign.VideoID),
                Width: campaign.Video.Width,
                Height: campaign.Video.Height,
                videoID: campaign.VideoID,
                campaignStatus: campaign.Status
            };
            model.videoStatus = campaign.VideoStatus;
        }

        model.simpleAudienceMode = campaign.SimpleAudienceMode;
        this.setSchedule(model.schedule, campaign);
        model.createDate = campaign.CreateDate;
        model.campaignContentType = campaign.ContentType;
        model.offerAudience = this.getClientAudience(campaign.Audience);
        return model;
    }

    public getDefaultCampaignModel = async (type?: number): Promise<ICampaign> => {
        const defaultCampaign = angular.copy(this.campaignModel);
        defaultCampaign.campaignContentType = 2;

        if (type) {
            defaultCampaign.type = type;
        }

        return defaultCampaign;
    }

    public updateCampaign = async (id: number, campaign: ICampaign, image: ICampaignImage, video: ICampaignVideo) => {
        if (this.isNeedUploadImage(campaign.campaignContentType, image)) {
            const uploadedImage = await this.uploadImage(image);
            campaign.image = {
                imageID: uploadedImage.ID,
                imageUrl: this.retailerService.getCampaignImageUrl(uploadedImage.ID),
                croppedImage: null,
                fileName: null,
                isGif: uploadedImage.Type === UploadedCampaignImage.TypeEnum.NUMBER_4
            };
            image.imageID = campaign.image.imageID;
            image.imageUrl = campaign.image.imageUrl;
            campaign.campaignContentType = uploadedImage.Type;
        }
        if (campaign.campaignContentType === this.enumService.CampaignContentType.Video && this.isSetCampaingVideo(video)) {
            campaign.video = {
                ID: video.ID,
                VideoUrl: video.VideoUrl,
                TeaserUrl: null,
                ThumbnailUrl: video.ThumbnailUrl,
                Width: video.Width,
                Height: video.Height,
                videoID: video.videoID,
                campaignStatus: video.campaignStatus
            };
        }
        return await this.http.put<number>(this.retailerService.campaign(id), this.toServerCampaign(campaign));
    }

    public setInitialCampaignImageData = (imageID: number, base64Img: string) => {
        if (this.initialCampaignImageData == null || this.initialCampaignImageData.id !== imageID) {
            this.initialCampaignImageData = {
                id: imageID,
                base64: base64Img
            };
        }
    }

    public deleteCampaign = (id: number) => {
        return this.http.delete<void>(this.retailerService.campaign(id));
    }

    public getCampaignVideoMetadata = (id: number): Promise<VideoMetadata> => {
        return this.http.get<VideoMetadata>(this.retailerService.campaignVideoGet(id));
    }

    public deleteCampaignVideo = (id: number) => {
        return this.http.delete<void>(this.retailerService.campaignVideoDelete(id));
    }

    public getImageUrl = (id: number): string => {
        return this.retailerService.getCampaignImageUrl(id);
    }

    public getUploadVideoUrl(): string {
        return this.retailerService.campaignVideo();
    }

    public getEditableCampaign = (id: number) => {
        if (this.editableCampaign && (!id || this.editableCampaign.id === id)) {
            return this.$q.when(this.editableCampaign);
        } else if (id) {
            return this.$q.when(this.getCampaign(id));
        } else {
            return this.$q.when(angular.copy(this.campaignModel));
        }
    }

    public setEditableCampaign = (value: ICampaign, isCopy: boolean) => {
        if (value == null) {
            this.editableCampaign = value;
        } else {
            const campaign = angular.copy(value);
            if (isCopy) {
                campaign.name += ' (copy)';
                campaign.nfcStickers = [];
                campaign.entityStatus = Campaign.EntityStatusEnum.NUMBER_1;
                if (campaign.schedule.endDate && (new Date(campaign.schedule.endDate.toDateString()).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0))) {
                    campaign.schedule.endDate = null;
                }
            }
            this.editableCampaign = campaign;
        }
    }

    public getImageByID = (id: number, responseType: any) => { // TODO responseType
        if (responseType) {
            return this.http.get(this.retailerService.getCampaignImageUrl(id), { responseType });
        } else {
            return this.http.get(this.retailerService.getCampaignImageUrl(id));
        }
    }

    public resizeFont = () => {
        const areaWidth = $('.template-table').width();
        if (areaWidth != null) {
            $('.template-table, .paddingTemlate').css('font-size', 0.1 * areaWidth);
        }

        const areaWidthImg = $('.widthSelector').width();
        if (areaWidthImg != null) {
            $('.widthSelector').css('font-size', 0.1 * areaWidthImg);
        }
    }

    public resizeFontWithTimeOut = () => {
        this.$timeout(this.resizeFont, 500);
        this.$timeout(this.resizeFont, 1000);
        this.$timeout(this.resizeFont, 1500);
    }

    public customScrollView = () => {
        this.$timeout(function() {
            $('.wrapPhoneInner').slimScroll({
                height: '417px',
                size: '4px',
                alwaysVisible: true
            });
        }, 600);
    }

    public setState = (campaignID: number, draft: boolean): angular.IPromise<void> => {
        return this.http.put(this.retailerService.campaignState(campaignID), draft);
    }

    public scrollTop = () => {
        $('body').animate({ scrollTop: 0 }, 400);
        $('.panel-body').animate({ scrollTop: 0 }, 400);
        $('.overflow-quick-camp').animate({ scrollTop: 0 }, 400);

    }

    public needHelp = () => {
        return this.$uibModal.open({
            component: 'designRequest',
            resolve: {},
            size: 'lg',
            windowClass: 'help-design'
        }).result;
    }

    public getDefaultCampaignSchedule(): ICampaignSchedule {
        const getBitArrayByNumber = this.commonCampaignsService.getBitArrayByNumber;
        const getTimeString = this.commonCampaignsService.getTimeString;
        const startTime = this.commonCampaignsService.getDateTime(null, '09:00');
        const endTime = this.commonCampaignsService.getDateTime(null, '18:00');

        const startDate = this.commonCampaignsService.getDateTime(null, '00:00');
        const endDate = moment(startDate).add(1, 'd').toDate();

        return {
            hasStartDate: false,
            hasEndDate: false,
            hasAlertDate: false,
            allDayStart: true,
            allDayEnd: true,
            startDate,
            alertDate: endDate,
            startTime,
            endDate,
            endTime,
            hasDaily: true,
            daysOfWeek: angular.copy(this.commonCampaignsService.getDaysOfWeek()),
            daysOfWeekInt: null,
            setDaysOfWeek(flag: boolean) {
                angular.forEach(this.daysOfWeek, (day) => {
                    day.selected = flag;
                });
                this.daysOfWeekInt = 0;
            },
            changeWeeksday() {
                let bit = '';
                angular.forEach(angular.copy(this.daysOfWeek).reverse(), (day) => {
                    bit += day.selected ? '1' : '0';
                });
                this.daysOfWeekInt = parseInt(bit, 2);
            },
            setSelectedDaysOfWeek(number: number) {
                const numberBit = getBitArrayByNumber(number).split('').reverse().join('');
                for (let c = 0; c < numberBit.length; c++) {
                    this.daysOfWeek[c].selected = numberBit[c] === '1';
                }
            },
            getStartDate() {
                return this.hasStartDate ? this.startDate : null;
            },
            getEndDate() {

                return this.hasEndDate ? this.endDate : null;
            },
            getDailyStartTime() {

                return this.allDayStart ? null : getTimeString(false, this.startTime);
            },
            getDailyEndTime() {
                return this.allDayEnd ? null : getTimeString(false, this.endTime);
            },
            updateState() {
                this.startTime = this.allDayStart ? startTime : this.startTime;
                this.endTime = this.allDayEnd ? endTime : this.endTime;
            }
        };
    }

    public getCountry(): any {
        return this.http.get(this.retailerService.country()).then((country: any) => {
            return country;
        });
    }

    public getAudience() {
        return this.http.get<AudienceResourceOfferModel>(this.retailerService.campaignAudience());
    }

    public getAudienceModel(audience: AudienceResourceOfferModel, offerAudience: IOfferAudience): IAudienceOffer {

        const model = {
            ages: this.getSimpleAudienceModel(audience.Age, offerAudience.ages, this.enumService.OfferAudienceMapping.ages),
            genders: this.getSimpleAudienceModel(audience.Gender, offerAudience.genders, this.enumService.OfferAudienceMapping.genders),
            interests: this.getSimpleAudienceModel(audience.Interest, offerAudience.interests, this.enumService.OfferAudienceMapping.interests),
            places: this.getPlaceAudienceModel(audience.Places, offerAudience.placeIDs),
            categories: this.getCategoryAudienceModel(audience.Categories, offerAudience.categoryIDs)
        } as IAudienceOffer;
        return model;
    }

    private getSimpleAudienceModel(resource: EnumListItem[], selected: number[], dictionary: any): IGroupAudience {

        const items = _.map(resource, (item) => {
            return {
                id: item.ID,
                title: dictionary ? dictionary[item.ID] : _.isEmpty(item.Title) ? item.Name : item.Title,
                selected: _.isEmpty(selected) ? true : _.contains(selected, item.ID)
            } as ISimpleAudience;
        });
        return {
            enabled: !_.isEmpty(selected),
            selected: _.all(items, (item) => item.selected),
            items
        };
    }

    private getPlaceAudienceModel(resource: AudienceOfferPlaceListItem[], selected: number[]): IPlacesGroupAudience {
        const items = _.map(resource, (item) => {
            return {
                id: item.ID,
                title: item.Name,
                town: item.Town,
                categoryIDs: item.CategoryIDs,
                isArchived: item.IsArchived,
                selected: _.isEmpty(selected) ? true : _.contains(selected, item.ID),
            } as IPlaceAudience;
        });
        return {
            enabled: !_.isEmpty(selected),
            selected: _.all(items, (item) => item.selected),
            items
        };
    }

    private getCategoryAudienceModel(resource: AudienceOfferCategoryListItem[], selected: number[]): IGroupAudience {
        const items = _.map(resource, (item) => {
            return {
                id: item.ID,
                title: item.FullName,
                name: item.Name,
                placeIDs: item.PlaceIDs,
                selected: _.isEmpty(selected) ? true : _.contains(selected, item.ID),
            } as ICategoryAudience;
        });
        return {
            enabled: !_.isEmpty(selected),
            selected: _.all(items, (item) => item.selected),
            items
        };
    }
    private isNewCampaingImage(image: ICampaignImage) {
        const isImageCropped = image != null && !_.isEmpty(image.croppedImage);
        const isCampaignImage = this.initialCampaignImageData != null && this.initialCampaignImageData.base64 != null;
        return (isImageCropped && !isCampaignImage) || (isImageCropped && isCampaignImage && this.initialCampaignImageData.base64 !== image.croppedImage);
    }

    private isSetCampaingVideo(video: ICampaignVideo) {
        return video != null && video.videoID > 0;
    }

    private replaceTags = (html) => {
        if (html) {
            return html.replace(/<font [^\>]*>/g, '').replace(/<\/font>/g, '').replace(/<span [^\>]*>/g, '<span>');
        } else {
            return html;
        }
    }

    private asUtc(local: Date) {
        return new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate(), local.getHours(), local.getMinutes()));
    }

    private uploadImage = async (image: ICampaignImage) => {
        if (!image || !image.croppedImage) {
            return this.$q.reject('Cropped image is error');
        }
        const blob = this.blobService.dataUriToBlob(image.croppedImage);
        if (blob == null) {
            return this.$q.reject('Cropped image is error');
        }
        const fd = new FormData();
        fd.append('FileImage', blob, image.fileName);

        return this.http.post<UploadedCampaignImage>(this.retailerService.campaignImage(), fd, {
            transformRequest: angular.identity,
            headers: { 'Content-Type': undefined }
        });

    }

    private convertFileToDataURLviaFileReader = (url: string, callback: (response: any) => void) => {
        const xhr = new XMLHttpRequest();
        xhr.onload = function() {
            const reader = new FileReader();
            reader.onloadend = function() {
                callback(reader.result);
            };
            reader.readAsDataURL(xhr.response);
        };
        xhr.open('GET', url);
        xhr.responseType = 'blob';
        xhr.send();
    }

    private getCampaignsPreview = async (url: string) => {
        try {
            const pagingResult = await this.http.get<CampaignsPagingResult>(url);
            _.forEach(pagingResult.Items, (campaign) => {
                this.commonCampaignsService.adaptCampaignData(campaign);
            });
            return pagingResult.Items as ICampaignListItem[];

        } catch (error) {
            return this.$q.reject(error);
        }

    }

    private setSchedule(schedule: ICampaignSchedule, campaign: Campaign) {
        const startTime = campaign.DailyStartTime ? this.commonCampaignsService.getDateTime(null, campaign.DailyStartTime) : schedule.startTime;
        const endTime = campaign.DailyEndTime ? this.commonCampaignsService.getDateTime(null, campaign.DailyEndTime) : schedule.endTime;

        schedule.hasStartDate = !!campaign.StartDate,
            schedule.hasEndDate = !!campaign.EndDate,
            schedule.hasAlertDate = !!campaign.AlertDate,

            schedule.startDate = this.commonCampaignsService.getDateTime(campaign.StartDate ? campaign.StartDate.toString() : null, '00:00'),
            schedule.endDate = this.commonCampaignsService.getDateTime(campaign.EndDate ? campaign.EndDate.toString() : null, '00:00'),
            schedule.alertDate = this.commonCampaignsService.getDateTime(schedule.hasAlertDate ? campaign.AlertDate.toString() : null, '00:00'),

            schedule.allDayStart = campaign.DailyStartTime == null || (startTime.getHours() === 0 && startTime.getMinutes() === 0),
            schedule.allDayEnd = campaign.DailyEndTime == null || (endTime.getHours() === 0 && endTime.getMinutes() === 0),

            schedule.startTime = startTime,
            schedule.endTime = endTime,

            schedule.hasDaily = campaign.DaysOfWeek == null,
            schedule.daysOfWeek = angular.copy(this.commonCampaignsService.getDaysOfWeek()),
            schedule.daysOfWeekInt = campaign.DaysOfWeek ? campaign.DaysOfWeek : 0;

        if (schedule.hasDaily) {
            schedule.setDaysOfWeek(true);
        } else {
            schedule.setSelectedDaysOfWeek(campaign.DaysOfWeek);
        }
    }

    private getDefaultStatusModel() {
        const statusUndefined = this.enumService.CampaignStatus.Undefined;
        return {
            status: statusUndefined,
            message: null,
            set(status: number, message?: string) {

                this.status = status ? status : statusUndefined;
                this.message = message ? message : null;
            },
            clear() {
                this.status = statusUndefined;
                this.message = null;
            }
        };
    }

    private isNeedUploadImage(campaignContentType: number, image: ICampaignImage) {
        return (campaignContentType === this.enumService.CampaignContentType.Image || campaignContentType === this.enumService.CampaignContentType.Gif) && !_.isEmpty(image.croppedImage) && this.isNewCampaingImage(image);
    }

    private getClientAudience(audience: AudienceOffer): IOfferAudience {
        if (audience) {
            return {
                ages: audience.Ages ? audience.Ages : [],
                genders: audience.Genders ? audience.Genders : [],
                interests: audience.Interests ? audience.Interests : [],
                placeIDs: audience.PlaceIDs ? audience.PlaceIDs : [],
                categoryIDs: audience.CategoryIDs ? audience.CategoryIDs : [],
            };
        }

        return {
            ages: [],
            genders: [],
            interests: [],
            placeIDs: [],
            categoryIDs: [],
        };
    }
}
angular
    .module('naut')
    .service('campaignsService', CampaignsService);



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/campaigns/services/campaigns.service.ts